import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Users, Smartphone, Monitor, DollarSign, TrendingUp,
  Sparkles, Loader2, RefreshCw, MessageSquare, BookOpen, Apple,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  Line, LineChart, CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const Tile = ({ label, value, sub, icon: Icon, accent = "text-mystic-300", testid }) => (
  <Card className="glass" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-mystic-200/50">{label}</p>
          <p className="text-3xl font-semibold text-white mt-2">{value}</p>
          {sub && <p className="text-xs text-mystic-200/60 mt-1.5">{sub}</p>}
        </div>
        <Icon className={`w-8 h-8 ${accent} opacity-70`} />
      </div>
    </CardContent>
  </Card>
);

const SignupSplitTile = ({ label, data, testid }) => {
  const total = data?.total ?? 0;
  const web = data?.web ?? 0;
  const mobile = data?.mobile ?? 0;
  const mobilePct = total > 0 ? Math.round((mobile / total) * 100) : 0;
  return (
    <Card className="glass" data-testid={testid}>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-widest text-mystic-200/50">{label}</p>
        <p className="text-3xl font-semibold text-white mt-2">{total}</p>
        <div className="flex items-center gap-4 mt-3 text-sm">
          <span className="flex items-center gap-1 text-blue-300">
            <Monitor className="w-3.5 h-3.5" /> {web}
          </span>
          <span className="flex items-center gap-1 text-pink-300">
            <Smartphone className="w-3.5 h-3.5" /> {mobile}
            <span className="text-mystic-200/60 ml-1">({mobilePct}%)</span>
          </span>
        </div>
        {/* Split bar */}
        <div className="mt-3 h-1.5 rounded-full bg-mystic-800 overflow-hidden flex">
          <div
            className="bg-blue-400"
            style={{ width: total ? `${(web / total) * 100}%` : "0%" }}
          />
          <div
            className="bg-pink-400"
            style={{ width: total ? `${(mobile / total) * 100}%` : "0%" }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboardPage = () => {
  const { token, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [daily, setDaily] = useState(null);
  const [txSummary, setTxSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, aRes, tRes, anRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/daily?days=30`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/transactions/summary`, { headers, withCredentials: true }).catch(() => ({ data: { by_status: [] } })),
        axios.get(`${API}/analytics/events/summary?days=30`, { headers, withCredentials: true }).catch(() => ({ data: { buckets: [], by_store: [] } })),
      ]);
      setDashboard(dRes.data);
      setDaily(aRes.data);
      setTxSummary(tRes.data);
      setAnalytics(anRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load admin dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-16 text-mystic-200">
        Unable to load dashboard. You may not have admin access.
      </div>
    );
  }

  const signups = dashboard.signups || {};
  const providers = dashboard.auth_providers || {};
  const revenue = dashboard.revenue || {};
  const chartData = (daily?.signups_by_day || []).map((d) => ({
    date: d.date.slice(5), // MM-DD
    Web: d.web,
    Mobile: d.mobile,
  }));

  // Aggregate mobile-app-banner funnel from analytics summary buckets.
  const sumEvent = (eventName) =>
    (analytics?.buckets || [])
      .filter((b) => b.event === eventName)
      .reduce((acc, b) => acc + (b.count || 0), 0);
  const bannerShown = sumEvent("mobile_app_banner_shown");
  const bannerDismissed = sumEvent("mobile_app_banner_dismissed");
  const bannerClicked = sumEvent("mobile_app_link_clicked");
  const ctr = bannerShown > 0 ? Math.round((bannerClicked / bannerShown) * 100) : 0;
  const dismissRate = bannerShown > 0 ? Math.round((bannerDismissed / bannerShown) * 100) : 0;
  const byStore = (analytics?.by_store || []);
  const storeCount = (name) =>
    byStore.find((s) => s.store === name)?.count ?? 0;
  const appStoreClicks = storeCount("app_store");
  const playStoreClicks = storeCount("play_store");
  const otherStoreClicks = bannerClicked - appStoreClicks - playStoreClicks;
  const variantRows = analytics?.banner_funnel_by_variant || [];
  // Highlight the leader (highest CTR among variants with >=10 impressions).
  const leaderVariant = variantRows
    .filter((v) => v.shown >= 10)
    .sort((a, b) => b.ctr_pct - a.ctr_pct)[0];

  return (
    <div className="space-y-8" data-testid="admin-dashboard-page">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <Link to="/admin" className="text-mystic-300 hover:text-white inline-flex items-center gap-1 text-sm mb-2" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Admin <span className="gradient-text">Dashboard</span>
          </h1>
          <p className="text-mystic-200/70 mt-1">Live signup, engagement, and revenue snapshot</p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          className="border-mystic-500/30 text-mystic-200 hover:text-white rounded-full"
          data-testid="refresh-admin-dashboard"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </motion.div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile
          label="Total Users"
          value={dashboard.total_users?.toLocaleString() || 0}
          icon={Users}
          accent="text-mystic-300"
          testid="tile-total-users"
        />
        <Tile
          label="Active Subs"
          value={dashboard.active_subscriptions?.toLocaleString() || 0}
          sub="paying premium users"
          icon={Sparkles}
          accent="text-amber-300"
          testid="tile-active-subs"
        />
        <Tile
          label="Lifetime Revenue"
          value={`$${(revenue.total_dollars || 0).toLocaleString()}`}
          sub={`${revenue.total_transactions || 0} transactions`}
          icon={DollarSign}
          accent="text-green-300"
          testid="tile-lifetime-revenue"
        />
        <Tile
          label="Forum Posts 7d"
          value={dashboard.forum_posts_7d || 0}
          sub={`${dashboard.journal_entries_7d || 0} journal entries`}
          icon={MessageSquare}
          accent="text-purple-300"
          testid="tile-forum-posts-7d"
        />
      </div>

      {/* Signup-source breakdown */}
      <div>
        <h2 className="text-lg text-white font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-mystic-300" />
          Signups by Source
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SignupSplitTile label="Today" data={signups.today} testid="signups-today" />
          <SignupSplitTile label="Last 7 Days" data={signups.last_7d} testid="signups-7d" />
          <SignupSplitTile label="Last 30 Days" data={signups.last_30d} testid="signups-30d" />
        </div>
      </div>

      {/* Signup chart */}
      <Card className="glass" data-testid="signups-chart-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-mystic-300" />
            Daily Signups (Web vs Mobile) — Last 30 Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-mystic-200/60 text-sm">No signups yet in this window.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#2d1b4e" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#a78bfa" fontSize={11} />
                <YAxis stroke="#a78bfa" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8 }}
                  labelStyle={{ color: "#e0b0ff" }}
                />
                <Legend />
                <Bar dataKey="Web" stackId="a" fill="#60a5fa" />
                <Bar dataKey="Mobile" stackId="a" fill="#f472b6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Auth provider breakdown + Transactions summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass" data-testid="auth-providers-card">
          <CardHeader>
            <CardTitle className="text-lg text-white">Auth Providers (lifetime)</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(providers).length === 0 ? (
              <p className="text-mystic-200/60 text-sm">No data yet.</p>
            ) : (
              <ul className="space-y-2">
                {Object.entries(providers)
                  .sort(([, a], [, b]) => b - a)
                  .map(([name, count]) => (
                    <li key={name} className="flex justify-between text-mystic-100" data-testid={`auth-provider-${name}`}>
                      <span className="capitalize">{name}</span>
                      <span className="font-semibold text-white">{count.toLocaleString()}</span>
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="glass" data-testid="transactions-summary-card">
          <CardHeader>
            <CardTitle className="text-lg text-white">Transactions by Status</CardTitle>
          </CardHeader>
          <CardContent>
            {(!txSummary?.by_status || txSummary.by_status.length === 0) ? (
              <p className="text-mystic-200/60 text-sm">No transactions yet.</p>
            ) : (
              <ul className="space-y-2">
                {txSummary.by_status.map((row) => (
                  <li key={row.status} className="flex justify-between text-mystic-100">
                    <span className="capitalize">{row.status || "unknown"}</span>
                    <span className="font-semibold text-white">
                      {row.count} <span className="text-mystic-300 text-sm">(${(row.amount_cents / 100).toFixed(2)})</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mobile App Banner Funnel — last 30 days */}
      <Card className="glass" data-testid="mobile-banner-funnel-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-fuchsia-300" />
            "Get the App" Banner Funnel — Last 30 Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bannerShown === 0 ? (
            <p className="text-mystic-200/60 text-sm">No banner impressions in this window yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="rounded-xl bg-mystic-800/40 p-4" data-testid="banner-impressions">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">Impressions</p>
                <p className="text-2xl font-semibold text-white mt-1">{bannerShown.toLocaleString()}</p>
              </div>
              <div className="rounded-xl bg-mystic-800/40 p-4" data-testid="banner-clicks">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">Store Clicks</p>
                <p className="text-2xl font-semibold text-emerald-300 mt-1">{bannerClicked.toLocaleString()}</p>
              </div>
              <div className="rounded-xl bg-mystic-800/40 p-4" data-testid="banner-ctr">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">CTR</p>
                <p className="text-2xl font-semibold text-emerald-300 mt-1">{ctr}%</p>
              </div>
              <div className="rounded-xl bg-mystic-800/40 p-4" data-testid="banner-dismissed">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">Dismissed</p>
                <p className="text-2xl font-semibold text-rose-300 mt-1">{bannerDismissed.toLocaleString()}</p>
              </div>
              <div className="rounded-xl bg-mystic-800/40 p-4" data-testid="banner-dismiss-rate">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">Dismiss Rate</p>
                <p className="text-2xl font-semibold text-rose-300 mt-1">{dismissRate}%</p>
              </div>
            </div>
          )}

          {/* Per-store click breakdown */}
          {bannerClicked > 0 && (
            <div className="mt-5 pt-4 border-t border-mystic-700/40">
              <p className="text-[11px] uppercase tracking-widest text-mystic-200/50 mb-3">
                Clicks by Store
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-mystic-800/30 p-3 flex items-center gap-3" data-testid="store-app-store">
                  <Apple className="w-5 h-5 text-mystic-200" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-mystic-200/50">App Store</p>
                    <p className="text-lg font-semibold text-white">{appStoreClicks.toLocaleString()}</p>
                  </div>
                </div>
                <div className="rounded-lg bg-mystic-800/30 p-3 flex items-center gap-3" data-testid="store-play-store">
                  <Smartphone className="w-5 h-5 text-emerald-300" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-mystic-200/50">Google Play</p>
                    <p className="text-lg font-semibold text-white">{playStoreClicks.toLocaleString()}</p>
                  </div>
                </div>
                <div className="rounded-lg bg-mystic-800/30 p-3 flex items-center gap-3" data-testid="store-other">
                  <MessageSquare className="w-5 h-5 text-mystic-300/60" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-mystic-200/50">Other/Unknown</p>
                    <p className="text-lg font-semibold text-white">{Math.max(0, otherStoreClicks).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* A/B variant funnel table */}
          {variantRows.length > 0 && (
            <div className="mt-5 pt-4 border-t border-mystic-700/40" data-testid="banner-ab-table">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-widest text-mystic-200/50">
                  A/B Test — by Variant
                </p>
                {leaderVariant && (
                  <span
                    className="text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2 py-0.5"
                    data-testid="banner-ab-leader"
                  >
                    🏆 Leader: {leaderVariant.variant} ({leaderVariant.ctr_pct}% CTR)
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-mystic-200/60 text-[11px] uppercase tracking-widest border-b border-mystic-700/40">
                      <th className="py-2 pr-3 font-normal">Variant</th>
                      <th className="py-2 pr-3 font-normal text-right">Impressions</th>
                      <th className="py-2 pr-3 font-normal text-right">Clicks</th>
                      <th className="py-2 pr-3 font-normal text-right">CTR</th>
                      <th className="py-2 pr-3 font-normal text-right">Dismissed</th>
                      <th className="py-2 font-normal text-right">Dismiss %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantRows.map((v) => {
                      const isLeader = leaderVariant?.variant === v.variant;
                      return (
                        <tr
                          key={v.variant}
                          className={`border-b border-mystic-800/40 ${isLeader ? "bg-emerald-500/5" : ""}`}
                          data-testid={`variant-row-${v.variant}`}
                        >
                          <td className="py-2 pr-3 text-white font-medium">
                            {v.variant}
                            {isLeader && <span className="ml-1.5 text-emerald-300">★</span>}
                          </td>
                          <td className="py-2 pr-3 text-right text-mystic-100">{v.shown.toLocaleString()}</td>
                          <td className="py-2 pr-3 text-right text-emerald-300">{v.clicked.toLocaleString()}</td>
                          <td className="py-2 pr-3 text-right text-emerald-300 font-semibold">{v.ctr_pct}%</td>
                          <td className="py-2 pr-3 text-right text-rose-300">{v.dismissed.toLocaleString()}</td>
                          <td className="py-2 text-right text-rose-300">{v.dismiss_rate_pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-mystic-200/40 mt-2">
                Leader badge appears when a variant has ≥10 impressions. Variants are assigned per-visitor via localStorage.
              </p>
            </div>
          )}
          <p className="text-[11px] text-mystic-200/40 mt-3">
            Source: <code className="text-mystic-200/60">analytics_events</code> · events: <code className="text-mystic-200/60">mobile_app_banner_shown · mobile_app_link_clicked · mobile_app_banner_dismissed</code>
          </p>
        </CardContent>
      </Card>

      <p className="text-mystic-200/40 text-xs text-center pt-4">
        Logged in as {user?.email} · Last refreshed{" "}
        {dashboard.generated_at ? new Date(dashboard.generated_at).toLocaleString() : "—"}
      </p>
    </div>
  );
};

export default AdminDashboardPage;
